import { FC } from "react"
import Breadcrumbs from "../../components/Breadcrumbs"
import Grid from "../../components/Grid"
import MainLayout from "../../layouts"
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import { ParsedUrlQuery } from 'querystring'
import { TrainerRatingStats, Workshop, WorkshopEdition } from '../../types/database'
import { getWorkshopBySlug, getWorkshopEditionsByWorkshopId } from '../../lib/database/workshops'
import { getCountOfResponsesForWorkshop, getTrainerRatingStats, getWorkshopAverageRating, getWorkshopEditionRatings } from '../../lib/database/polls'
import { getWorkshopAgenda } from '../../lib/getWorkshopAgenda'
import TitleBarWithComponent from '../../components/TitleBarWithComponent'
import CourseMeta from '../../components/CourseMeta'
import styles from '../../styles/Course.module.scss';
import Image from 'next/image';
import pig from '../../public/icons/pig-money.svg';
import clock from '../../public/icons/clock-4.svg';
import gps from '../../public/icons/gps.svg';
import star from '../../public/icons/star.svg';
import Button from '../../components/Button';
import { ButtonType } from '../../components/Button/Button.types'
import TrainingProgram from '../../components/TrainingProgram'
import { useTranslations } from "../../hooks/useTranslations"
import { formatDate } from "../../utils/formatDate"
import { WorkshopAudienceSection, WorkshopEditionListItem, WorkshopEditionsSection, WorkshopTrainerSection } from '../../components/pages/workshops/ui'

interface WorkshopPageProps {
    workshop: Workshop | null,
    averageRating: number | null,
    trainerRatingStats: TrainerRatingStats | null,
    numberOfResponses: number | null,
    agendaContent: string | null,
    editions: WorkshopEditionListItem[],
    locale: string,
}

const getDateKey = (date: string) => new Date(date).toISOString().slice(0, 10);

const WorkshopPage: FC<WorkshopPageProps> = ({ workshop, averageRating, trainerRatingStats, numberOfResponses, agendaContent, editions, locale }) => {
    const { t, translateByFullKey, translate } = useTranslations();

    if (!workshop) {
        return (
            <MainLayout head={{}} hideOverflow>
                <Grid>
                    <Breadcrumbs />
                    <h1>Warsztat nie został znaleziony</h1>
                </Grid>
            </MainLayout>
        );
    }

    const {
        name: title,
        longDescription,
        days,
        currency,
        costPerUser,
        maxParticipants,
        cityOrRemote,
    } = workshop;

    const titleText = translateByFullKey(title);
    const longDescriptionText = translateByFullKey(longDescription || '');
    const formatEditionDate = (date: string) => formatDate({
        dateObject: date,
        formatString: 'dd MMMM yyyy',
        locale,
    });

    return (
        <MainLayout head={{ title: titleText || 'Warsztat' }} hideOverflow>
            <Grid>
                <Breadcrumbs />
                <TitleBarWithComponent title={<>{titleText}</>} text={longDescriptionText} capitalize={false}>
                    <div className={styles.MetaData}>
                        {days && <CourseMeta
                            label={t.WORKSHOP.DURATION}
                            value={`${days} ${days > 1 ? t.COMMON.DAYS : t.COMMON.DAY}`}
                            icon={<Image src={clock || `/icons/clock.svg`} alt="" height={32} width={32} />}
                        />}
                        {costPerUser && <CourseMeta
                            label={t.WORKSHOP.COST_PER_USER}
                            value={`${costPerUser} ${currency || 'PLN'} ${t.WORKSHOP.NET}`}
                            valueBelow={t.WORKSHOP.COST_NOTE}
                            icon={<Image src={pig || `/icons/pig-money.svg`} alt="" height={32} width={32} />}
                        />}
                        {cityOrRemote && <CourseMeta
                            label={t.WORKSHOP.CITY_OR_REMOTE}
                            value={cityOrRemote}
                            icon={<Image src={gps || `/icons/gps.svg`} alt="" height={32} width={32} />}
                        />}
                        {averageRating > 0 && <CourseMeta
                            label={t.WORKSHOP.AVERAGE_RATING}
                            value={`${averageRating} / 5`}
                            valueBelow={numberOfResponses ? translate({ value: t.WORKSHOP.BASED_ON_RESPONSES, variables: [ numberOfResponses.toString() ] }) : ''}
                            icon={<Image src={star || `/icons/star.svg`} alt="" height={32} width={32} />}
                        />}
                        <Button.L pattern={ButtonType.LIGTHENED} label={t.WORKSHOP.ASK_FOR_PRICE} href="/contact" />
                    </div>
                </TitleBarWithComponent>
                <WorkshopAudienceSection
                    title={<>{t.WORKSHOP.AUDIENCE.TITLE}<br/><strong>{t.WORKSHOP.AUDIENCE.TITLE_STRONG}</strong></>}
                    description={t.WORKSHOP.AUDIENCE.DESCRIPTION}
                    forTitle={t.WORKSHOP.AUDIENCE.FOR_TITLE}
                    forItems={t.WORKSHOP.AUDIENCE.FOR_ITEMS}
                    notForTitle={t.WORKSHOP.AUDIENCE.NOT_FOR_TITLE}
                    notForItems={t.WORKSHOP.AUDIENCE.NOT_FOR_ITEMS}
                />
                <WorkshopTrainerSection
                    eyebrow={t.WORKSHOP.TRAINER.EYEBROW}
                    title={t.WORKSHOP.TRAINER.TITLE}
                    name={t.WORKSHOP.TRAINER.NAME}
                    description={t.WORKSHOP.TRAINER.DESCRIPTION}
                    experienceValue={t.WORKSHOP.TRAINER.EXPERIENCE_VALUE}
                    experienceLabel={t.WORKSHOP.TRAINER.EXPERIENCE_LABEL}
                    teachingSinceValue={t.WORKSHOP.TRAINER.TEACHING_SINCE_VALUE}
                    teachingSinceLabel={t.WORKSHOP.TRAINER.TEACHING_SINCE_LABEL}
                    rating={trainerRatingStats?.averageRating || null}
                    ratingLabel={trainerRatingStats?.responsesCount ? translate({
                        value: t.WORKSHOP.TRAINER.RATING_LABEL,
                        variables: [ trainerRatingStats.responsesCount.toString() ],
                    }) as string : t.WORKSHOP.TRAINER.RATING_LABEL_WITHOUT_RESPONSES}
                    actionLabel={t.WORKSHOP.TRAINER.ACTION}
                />
                {agendaContent && <TrainingProgram content={agendaContent}/>}
                {editions.length > 0 && <WorkshopEditionsSection
                    title={<>{t.WORKSHOP.EDITIONS}<br/><strong>{t.WORKSHOP.EDITIONS_STRONG}</strong></>}
                    description={t.WORKSHOP.EDITIONS_DESCRIPTION}
                    totalLabel={t.WORKSHOP.TOTAL_EDITIONS}
                    editions={editions}
                    averageRatingLabel={t.WORKSHOP.AVERAGE_RATING_SHORT}
                    plannedEditionLabel={t.WORKSHOP.PLANNED_EDITION}
                    openPlaceLabel={t.WORKSHOP.OPEN_EDITION_PLACE}
                    formatEditionDate={formatEditionDate}
                />}
                {/* TODO: Add reviews from database */}
                {/* <WorkshopReviews reviews={[]} /> */}
            </Grid>
        </MainLayout>
    )
}

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext<ParsedUrlQuery>) => {
    const { slug } = context.params || {};

    if (!slug || typeof slug !== 'string') {
        return {
            notFound: true,
        };
    }

    try {
        const workshop = await getWorkshopBySlug(slug);

        if (!workshop) {
            return {
                notFound: true,
            };
        }

        const averageRating = await getWorkshopAverageRating(workshop.id);
        const trainerRatingStats = await getTrainerRatingStats();
        const numberOfResponses = await getCountOfResponsesForWorkshop(workshop.id);
        const editions = await getWorkshopEditionsByWorkshopId(workshop.id);
        const editionRatings = await getWorkshopEditionRatings(workshop.id);
        const editionsWithRatings = editions.map((edition) => {
            const rating = editionRatings.find((editionRating) => (
                (edition.source === 'past_workshops' && edition.sourceId === editionRating.pastWorkshopId.toString())
                || (edition.pollAccessCode && edition.pollAccessCode === editionRating.pollAccessCode)
                || getDateKey(edition.startsAt) === getDateKey(editionRating.date)
            ));

            return {
                ...edition,
                averageRating: rating?.averageRating || null,
            };
        });
        
        const locale = context.locale || 'pl';
        const agenda = getWorkshopAgenda(slug, locale);

        return {
            props: {
                workshop,
                averageRating,
                trainerRatingStats,
                numberOfResponses,
                agendaContent: agenda?.content || null,
                editions: editionsWithRatings,
                locale,
            }
        };
    } catch (error) {
        console.error('Error fetching workshop:', error);
        return {
            notFound: true,
        };
    }
}

export default WorkshopPage;
